import type {
  BirthInput,
  LifeEvent,
  LifeEventType,
  Planet,
  RectificationResult,
  RectificationWindow,
} from '@astrally/shared';
import { getDefaultAdapter, type EphemerisAdapter } from '../ephemeris/index.js';
import { zonedToUtc } from '../time/timezone.js';
import { getAyanamsa } from '../ayanamsa/lahiri.js';
import { norm360 } from '../math/angles.js';
import { signCount, signOf } from '../core/zodiac.js';
import { computeAngles } from '../core/houses.js';
import { activePeriods, computeVimshottari } from '../jyotish/vimshottari.js';

/**
 * Birth-time refinement foundation.
 *
 * Heuristic scorers compare candidate birth times against dated life events:
 *  1. Dasha-theme scorer — was the event date inside a mahadasha/antardasha of
 *     a lord classically associated with that event theme?
 *  2. Angle-transit scorer — was Jupiter/Saturn transiting the candidate
 *     lagna or its 10th sign around the event? (Differentiates times within a
 *     day, since only the lagna varies.)
 *
 * The output is ALWAYS a set of scored windows with explanations and
 * preserved uncertainty — never "the discovered exact time".
 */

const EVENT_DASHA_LORDS: Record<LifeEventType, Planet[]> = {
  'major-move': ['rahu', 'moon', 'saturn'],
  'first-job': ['saturn', 'sun', 'mercury'],
  'career-shift': ['saturn', 'sun', 'jupiter', 'rahu'],
  marriage: ['venus', 'jupiter', 'rahu'],
  'relationship-start': ['venus', 'moon', 'rahu'],
  separation: ['saturn', 'ketu', 'mars'],
  'education-milestone': ['mercury', 'jupiter'],
  'sibling-event': ['mars', 'mercury'],
  'family-event': ['moon', 'jupiter'],
  'accident-or-surgery': ['mars', 'saturn', 'ketu'],
  'business-launch': ['mercury', 'mars', 'rahu'],
};

export interface RectifyOptions {
  adapter?: EphemerisAdapter;
  /** Candidate spacing in minutes. */
  stepMinutes?: number;
  /** Number of windows to return. */
  topWindows?: number;
}

export const RECTIFICATION_DISCLAIMER =
  'Based on the events you supplied, these time windows fit better than the alternatives. ' +
  'Treat them as estimates until they can be confirmed from records or further events.';

export function rectifyBirthTime(
  input: BirthInput,
  events: LifeEvent[],
  opts: RectifyOptions = {},
): RectificationResult {
  const adapter = opts.adapter ?? getDefaultAdapter();
  const step = opts.stepMinutes ?? 15;
  const { date, place } = input;

  const window =
    input.timeInput.kind === 'approximate'
      ? input.timeInput.window
      : { start: { hour: 0, minute: 0 }, end: { hour: 23, minute: 59 } };

  const utcStart = zonedToUtc(
    { year: date.year, month: date.month, day: date.day, ...window.start },
    place.timezone,
  );
  let utcEnd = zonedToUtc(
    { year: date.year, month: date.month, day: date.day, ...window.end },
    place.timezone,
  );
  if (utcEnd.getTime() <= utcStart.getTime()) utcEnd = new Date(utcEnd.getTime() + 86400000);

  // Pre-compute slow transit signs per event (independent of candidate time).
  const eventTransits = events.map((ev) => {
    const at = new Date(ev.dateISO);
    if (Number.isNaN(at.getTime())) return null;
    const ayan = getAyanamsa('lahiri').valueDeg(adapter.julianCenturiesTT(at));
    return {
      event: ev,
      at,
      jupiterSign: signOf(norm360(adapter.position('jupiter', at).lon - ayan)),
      saturnSign: signOf(norm360(adapter.position('saturn', at).lon - ayan)),
    };
  });

  interface Candidate {
    utc: Date;
    minutes: number;
    score: number;
    notes: Set<string>;
  }
  const candidates: Candidate[] = [];

  for (let t = utcStart.getTime(); t <= utcEnd.getTime(); t += step * 60000) {
    const utc = new Date(t);
    const ayan = getAyanamsa('lahiri').valueDeg(adapter.julianCenturiesTT(utc));
    const moonSid = norm360(adapter.position('moon', utc).lon - ayan);
    const vim = computeVimshottari(moonSid, utc, 365.25, 2);
    const angles = computeAngles(utc, place.lat, place.lon, adapter, ayan);
    const lagnaSign = signOf(angles.ascendantSidereal);

    let score = 0;
    const notes = new Set<string>();

    for (const et of eventTransits) {
      if (!et) continue;
      const themeLords = EVENT_DASHA_LORDS[et.event.type];

      // 1. Dasha-theme fit
      const chain = activePeriods(vim, et.at);
      const maha = chain[0];
      const antar = chain[1];
      if (maha && themeLords.includes(maha.lord)) {
        score += 2;
        notes.add(`${et.event.type} fell in a ${maha.lord} mahadasha`);
      }
      if (antar && themeLords.includes(antar.lord)) {
        score += 3;
        notes.add(`${et.event.type} fell in a ${antar.lord} antardasha`);
      }

      // 2. Angle-transit fit (varies with candidate lagna)
      for (const [planet, sign] of [
        ['Jupiter', et.jupiterSign],
        ['Saturn', et.saturnSign],
      ] as const) {
        const fromLagna = signCount(lagnaSign, sign);
        if (fromLagna === 1 || fromLagna === 10) {
          score += 1.5;
          notes.add(`${planet} was transiting house ${fromLagna} from this lagna at ${et.event.type}`);
        }
      }
    }

    candidates.push({
      utc,
      minutes: Math.round((t - utcStart.getTime()) / 60000),
      score,
      notes,
    });
  }

  if (candidates.length === 0 || events.length === 0) {
    return { windows: [], disclaimer: RECTIFICATION_DISCLAIMER, eventsUsed: events.length };
  }

  // Group contiguous candidates by lagna-window and aggregate scores.
  const maxScore = Math.max(...candidates.map((c) => c.score), 1);
  const threshold = maxScore * 0.7;
  const windows: RectificationWindow[] = [];
  let runStart: Candidate | null = null;
  let runNotes = new Set<string>();
  let runPeak = 0;

  const label = (minutes: number): string => {
    const base = window.start.hour * 60 + window.start.minute + minutes;
    const total = base % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  for (let i = 0; i <= candidates.length; i++) {
    const c = candidates[i];
    const qualifies = c !== undefined && c.score >= threshold && c.score > 0;
    if (qualifies) {
      if (!runStart) {
        runStart = c;
        runNotes = new Set();
        runPeak = 0;
      }
      c.notes.forEach((n) => runNotes.add(n));
      runPeak = Math.max(runPeak, c.score);
    } else if (runStart) {
      const prev = candidates[i - 1];
      windows.push({
        startLocal: label(runStart.minutes),
        endLocal: label(prev.minutes),
        score: Number((runPeak / maxScore).toFixed(2)),
        explanation: [...runNotes].slice(0, 6),
      });
      runStart = null;
    }
  }

  windows.sort((a, b) => b.score - a.score);

  return {
    windows: windows.slice(0, opts.topWindows ?? 3),
    disclaimer: RECTIFICATION_DISCLAIMER,
    eventsUsed: events.length,
  };
}
